import Config from "../config";

const ALLOWED_DOMAINS = ["globo.com", "globoi.com"];
const DEFAULT_CALLBACK_URL = "https://globoplay.globo.com";
const CLARO_OFFER_PATH = "/offer-claro";

const isLocalHostname = (hostname: string) =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname.endsWith(".local.globoi.com");

const isAllowedHostname = (hostname: string, allowedHosts: string[]) => {
  const normalizedHostname = hostname.toLowerCase().replace(/\.$/, "");

  return allowedHosts.some(allowedHost => {
    const normalizedAllowedHost = allowedHost.toLowerCase().replace(/\.$/, "");

    return (
      normalizedHostname === normalizedAllowedHost ||
      normalizedHostname.endsWith(`.${normalizedAllowedHost}`)
    );
  });
};

const parseUrl = (value: string | null) => {
  if (!value) return undefined;

  try {
    return new URL(value);
  } catch {
    return undefined;
  }
};

/**
 * Validates an absolute redirect URL for the current environment.
 * External redirects must use HTTPS and never include credentials.
 */
export function isValidRedirectUrl(value: string | null): boolean {
  const url = parseUrl(value);
  if (!url || url.username || url.password) {
    return false;
  }

  if (!isAllowedHostname(url.hostname, Config.ALLOWED_REDIRECT_HOSTS)) {
    return false;
  }

  return url.protocol === "https:" || isLocalHostname(url.hostname);
}

/**
 * Validates the persisted Claro action before the offer is opened.
 */
export function isValidClaroAction(host: string, path: string): boolean {
  const url = parseUrl(host);
  if (!url || url.username || url.password || path !== CLARO_OFFER_PATH) {
    return false;
  }

  if (url.pathname !== "/" || url.search || url.hash) {
    return false;
  }

  if (
    !isAllowedHostname(url.hostname, Config.ALLOWED_ACTION_HOSTS) &&
    !isLocalHostname(url.hostname)
  ) {
    return false;
  }

  return url.protocol === "https:" || isLocalHostname(url.hostname);
}

/**
 * Checks if the provided callback URL is valid and belongs to one of the allowed domains.
 * The URL must use either the "http:" or "https:" protocol and its hostname must match or be a subdomain of an allowed domain.
 *
 * @param callbackUrl - The callback URL to validate.
 * @returns `true` if the URL is valid and belongs to an allowed domain, otherwise `false`.
 */
export function isValidCallbackUrl(callbackUrl: string): boolean {
  const url = parseUrl(callbackUrl);
  if (!url || url.username || url.password) {
    return false;
  }

  const isProtocolSafe =
    url.protocol === "https:" ||
    (url.protocol === "http:" &&
      ALLOWED_DOMAINS.some(
        domain => url.hostname === domain || url.hostname.endsWith(`.${domain}`)
      ));

  return isProtocolSafe && isAllowedHostname(url.hostname, ALLOWED_DOMAINS);
}

/**
 * Returns a safe callback URL by validating the provided URL.
 * If the URL is null or invalid, it returns the default callback URL.
 *
 * @param url - The callback URL to validate.
 * @returns The validated callback URL or the default callback URL if invalid.
 */
export function getSafeCallbackUrl(url: string | null): string {
  if (!url) return DEFAULT_CALLBACK_URL;
  return isValidCallbackUrl(url) ? url : DEFAULT_CALLBACK_URL;
}

export function getSafeRedirectUrl(url: string | null): string | null {
  return isValidRedirectUrl(url) ? url : null;
}
